"use client";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, FileUpIcon, PlusIcon, TrashIcon } from "lucide-react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useImportReconciliationMutation } from "../api/reconciliation-queries";

const SOURCE_TYPE_OPTIONS = [
  { value: "bank_statement", label: "ໃບແຈ້ງຍອດທະນາຄານ" },
  { value: "payment_provider", label: "Payment Provider" },
  { value: "manual", label: "ມານວລ" },
];

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK (₭)" },
  { value: "THB", label: "THB (฿)" },
  { value: "USD", label: "USD ($)" },
];

const RecordSchema = z.object({
  sourceType: z.enum(["bank_statement", "payment_provider", "manual"]),
  sourceRef: z.string().min(1, "ຕ້ອງລະບຸ"),
  amount: z.coerce.number().min(0, "ຕ້ອງ ≥ 0"),
  currency: z.enum(["LAK", "THB", "USD"]).default("LAK"),
  txDate: z.string().min(1, "ຕ້ອງລະບຸ"),
  description: z.string().max(500).optional(),
});

const FormSchema = z.object({
  records: z.array(RecordSchema).min(1),
});

type FormValues = z.infer<typeof FormSchema>;

export function ReconciliationImportPage() {
  const nav = useNavigate();
  const mutation = useImportReconciliationMutation();

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      records: [
        {
          sourceType: "bank_statement",
          sourceRef: "",
          amount: 0,
          currency: "LAK",
          txDate: new Date().toISOString().slice(0, 10),
          description: "",
        },
      ],
    },
  });

  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "records",
  });

  const watchedRecords = methods.watch("records");
  const totalAmount = watchedRecords.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  function onSubmit(values: FormValues) {
    mutation.mutate(values.records, {
      onSuccess: () => nav({ to: "/app/wallet/reconciliation" }),
    });
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ການຕິດຕາມກັນ", to: "/app/wallet/reconciliation" },
            { label: "ນຳເຂົ້າ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav({ to: "/app/wallet/reconciliation" })}
          >
            <ArrowLeftIcon className="size-4" />
          </Button>
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ນຳເຂົ້າລາຍການ</h2>
            <p className="text-muted-foreground">
              ປ້ອນລາຍການຈາກໃບແຈ້ງຍອດ ຫຼື payment provider
            </p>
          </div>
        </div>

        <FormRoot
          methods={methods}
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          {fields.map((field, index) => (
            <Card key={field.id} className="rounded-xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">ລາຍການ #{index + 1}</CardTitle>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <TrashIcon className="size-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormSelect
                    name={`records.${index}.sourceType`}
                    label="ແຫຼ່ງ"
                    options={SOURCE_TYPE_OPTIONS}
                  />
                  <FormInput
                    name={`records.${index}.sourceRef`}
                    label="ເລກອ້າງອີງ"
                    placeholder="REF-001"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <FormPrice
                    name={`records.${index}.amount`}
                    label="ຈຳນວນເງິນ"
                    placeholder="0"
                    currencySymbol="K"
                  />
                  <FormSelect
                    name={`records.${index}.currency`}
                    label="ສະກຸນເງິນ"
                    options={CURRENCY_OPTIONS}
                  />
                  <FormInput
                    name={`records.${index}.txDate`}
                    label="ວັນທີທຸລະກຳ"
                    type="date"
                  />
                </div>

                <FormTextarea
                  name={`records.${index}.description`}
                  label="ລາຍລະອຽດ (ບໍ່ບັງຄັບ)"
                  placeholder="ບັນທຶກເພີ່ມເຕີມ..."
                />
              </CardContent>
            </Card>
          ))}

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                append({
                  sourceType: "bank_statement",
                  sourceRef: "",
                  amount: 0,
                  currency: "LAK",
                  txDate: new Date().toISOString().slice(0, 10),
                  description: "",
                })
              }
            >
              <PlusIcon className="size-4" /> ເພີ່ມລາຍການ
            </Button>

            <div className="text-right">
              <Label className="text-muted-foreground text-xs">ລວມ</Label>
              <p className="font-bold text-lg tabular-nums">
                {formatPrice(totalAmount, "LAK")}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => nav({ to: "/app/wallet/reconciliation" })}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              <FileUpIcon className="size-4" /> ນຳເຂົ້າ {fields.length} ລາຍການ
            </Button>
          </div>
        </FormRoot>
      </Main>
    </>
  );
}
